import { loadingToast } from '@side/pantry';
import { resizeImage } from 'src/components/UploadImageModal/helpers';
import type { GQLUpdateIdentityUserAvatarInput } from 'src/generated/graph-api';
import { useIdentityMutate } from '../../modules/api-clients/hooks';
import { useUpdateUserAvatarMutation } from './UpdateUserAvatar.generated';

export const avatarErrorsMap = {
  unknown: 'Something went wrong. Please try again.',
  // Graph has a max payload size limit, which might be reached by a file upload
  '413': 'File size too large, try a smaller file.',
};

export function useUpdateUserAvatar(userId: string) {
  const [mutate, options] = useUpdateUserAvatarMutation();
  const mutateIdentity = useIdentityMutate();

  // Invalidate identity user cache so anywhere it is loaded is refetched
  const invalidateIdentityUser = () =>
    mutateIdentity(['/users/{userId}', { params: { path: { userId } } }]);

  return [
    async (updateAvatarInput: GQLUpdateIdentityUserAvatarInput) => {
      const { file } = updateAvatarInput;
      let resizedImage: string | null = null;
      if (file) {
        resizedImage = await resizeImage(file);
      }

      if (!userId) {
        throw new Error('Cannot create mutation for unknown user');
      }
      return loadingToast(
        mutate({
          variables: {
            input: { ...updateAvatarInput, file: resizedImage },
            identityUserId: userId,
          },
        }).then((response) => {
          // Here we can access the coerced error message from graphql
          if (Array.isArray(response.errors) && response.errors[0].extensions) {
            return Promise.reject(
              avatarErrorsMap[
                (response.errors[0].extensions
                  .status as keyof typeof avatarErrorsMap) ?? 'unknown'
              ],
            );
          }
          return invalidateIdentityUser().then(() => response);
        }),
        {
          error: (error) =>
            error instanceof Error ? error.message : avatarErrorsMap.unknown,
          loading: 'Updating Avatar',
          success: 'Successfully Updated Avatar',
        },
        {
          id: 'updateAvatar',
        },
      );
    },
    options,
  ] as const;
}
